import React, { useState, useRef, useEffect } from 'react';

import { IBdsSelectionItem } from '../../../interfaces/bdsSelectionItem';
import BdsRadioButton from '../BdsRadioButton/BdsRadioButton';
import './BdsSelectionItem.scss';

export const BdsSelectionItem = ({ leadingData, data }: IBdsSelectionItem) => {
  const [ripples, setRipples] = useState<any[]>([]);
  const [isRippleActive, setIsRippleActive] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (rippleIntervalRef.current) {
        clearInterval(rippleIntervalRef.current);
      }
    };
  }, []);

  const calculateRipplePosition = (e: React.MouseEvent, rect: DOMRect) => {
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    return { size, x, y };
  };

  const createRipple = (e: React.MouseEvent) => {
    if (!buttonRef.current || isRippleActive) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const { size, x, y } = calculateRipplePosition(e, rect);

    const newRipple = {
      id: Date.now(),
      style: {
        width: size,
        height: size,
        left: x,
        top: y
      }
    };

    addRipple(newRipple);
    startRippleTimeout(newRipple);
  };

  const addRipple = (newRipple: { id: number; style: React.CSSProperties }) => {
    setRipples((prevRipples) => [...prevRipples, newRipple]);
    setIsRippleActive(true);
  };

  const startRippleTimeout = (newRipple: { id: number }) => {
    setTimeout(() => {
      if (isMounted.current) {
        removeRipple(newRipple.id);
      }
    }, 600);
  };

  const removeRipple = (rippleId: number) => {
    setRipples((prevRipples) => prevRipples.filter((ripple) => ripple.id !== rippleId));
    setIsRippleActive(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isRippleActive) {
      createRipple(e);
    }
    setIsPressed(true);
    if (rippleIntervalRef.current != null) {
      rippleIntervalRef.current = setInterval(() => {
        if (!isRippleActive) {
          createRipple(e);
          setIsRippleActive(true);
        }
      }, 150);
    }
  };

  const handleMouseUp = () => {
    if (rippleIntervalRef.current) {
      clearInterval(rippleIntervalRef.current);
      rippleIntervalRef.current = null;
    }
    setIsPressed(false);
    setIsRippleActive(false);
  };

  const handleClick = () => {
    data.onChange(data.value);
  };

  const renderSubtitle = () => {
    if (typeof leadingData.subtitle === 'string') {
      return <p className="s">{leadingData.subtitle}</p>;
    }
    if (Array.isArray(leadingData.subtitle)) {
      return (
        <div className="s">
          {leadingData.subtitle.slice(0, 2).map((item, index) => (
            <React.Fragment key={`k-${item}`}>
              <p className={index === 0 ? 'first' : ''}>{item}</p>
              {index === 0 && Array.isArray(leadingData.subtitle) && leadingData.subtitle.length > 1 && (
                <span className="separator"> • </span>
              )}
            </React.Fragment>
          ))}
          {leadingData.subtitle.slice(2).map((item) => (
            <p key={`${item}`}>{item}</p>
          ))}
        </div>
      );
    }
    return <div className="s">{leadingData.subtitle}</div>;
  };

  return (
    <button
      ref={buttonRef}
      className={`bds-selection-item ${isPressed ? 'pressed' : ''}`}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {ripples.map((ripple) => (
        <div key={ripple.id} className="ripple" style={ripple.style}></div>
      ))}
      <div className="texts">
        <p className="t">{leadingData.title}</p>
        {renderSubtitle()}
      </div>
      <BdsRadioButton
        name={data.value}
        value={data.value}
        selected={data.selected}
        onChange={data.onChange}
        variant="primary"
      />
    </button>
  );
};
